import {
  ADDON_CONTENTS,
  CONFIRMATIONS,
  DOCUMENTS,
  LEVEL_CONTENTS,
  LEVEL_ORDER,
  ROLE_PRESETS,
  confirmationsNeeded,
  documentForRole,
  itemsFor,
  itemsWithheld,
  normaliseAddons,
  shareAddon,
  type ConfirmationId,
  type Share,
  type ShareAccessLogEntry,
  type ShareAddon,
  type ShareLevel,
  type ShareRole,
} from '@adhd/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';

/**
 * Circle: the one surface for sharing. Release 5 of 2.0 made the card; the
 * fixes pass (13 October 2026, docs/pitch-2026-10-12/fixes/mocks/sharing.html)
 * folded the September share screen into it and deleted that screen.
 *
 * The ladder's machinery is untouched — grants per link, each widening asked
 * by `confirmationsNeeded` and refused by the server without the answer, a
 * link never widens on its own, revoke, the access log, the partner rule.
 * What changed is that every one of those is on the person's card, in the
 * card's words, behind one "Change ›", instead of on a second page with a
 * second vocabulary. "Someone else…" is the full ladder with no role.
 */

const EXPIRY_OPTIONS: { days: number | null; label: string }[] = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: 'A year' },
  { days: null, label: 'Never' },
];

const confirmationName = (id: ConfirmationId): string =>
  id in ADDON_CONTENTS ? ADDON_CONTENTS[id as ShareAddon].name : LEVEL_CONTENTS[id as Exclude<ConfirmationId, ShareAddon>].name;

function neededIn(e: unknown): ConfirmationId[] | null {
  if (!(e instanceof ApiError) || e.code !== 'confirmation_required') return null;
  const needed = (e.details as { needed?: unknown } | undefined)?.needed;
  return Array.isArray(needed) ? needed.filter((n): n is ConfirmationId => typeof n === 'string' && n in CONFIRMATIONS) : null;
}

const isDead = (l: Share): boolean => Boolean(l.revokedAt) || Boolean(l.expiresAt && Date.parse(l.expiresAt) < Date.now());

export function Circle(): JSX.Element {
  const [shares, setShares] = useState<Share[] | null>(null);
  const [off, setOff] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<(Share & { url: string }) | null>(null);

  const load = (): void => {
    api
      .listShares()
      .then(setShares)
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.isNotConfigured) setOff(true);
        else setError(e instanceof ApiError ? e.message : "Couldn't load your links.");
      });
  };
  useEffect(load, []);

  if (off) {
    return (
      <div className="stack-lg stack">
        <header className="page-header">
          <h1>Sharing is off</h1>
          <p>Whoever runs this server has turned sharing off, so there are no links to make here. Nothing you have written can leave this server by link.</p>
        </header>
        <div className="banner">If this server is yours, sharing is the <strong>FEATURE_SHARING</strong> setting.</div>
      </div>
    );
  }

  const live = (shares ?? []).filter((s) => !isDead(s));
  const dead = (shares ?? []).filter(isDead);

  return (
    <div className="stack-lg stack">
      <header className="page-header">
        <h1>Circle</h1>
        <p>Who can see how you are, and what each of them gets. You decide how much a link shows and how long it lasts, you can change it or switch it off at any time, and you can see every time it is opened. What has already been read, you cannot take back.</p>
      </header>

      {error && <div className="banner banner-error">{error}</div>}

      {made && <MadeLink made={made} onDone={() => setMade(null)} />}

      {shares === null ? (
        <div style={{ display: 'grid', placeItems: 'center', minHeight: '20dvh' }}>
          <span className="spinner" />
        </div>
      ) : live.length === 0 ? (
        <p className="muted">Nobody holds a link yet.</p>
      ) : (
        <ul className="stack stack-lg circle-people" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {live.map((s) => (
            <PersonCard key={s.id} share={s} onChanged={load} />
          ))}
        </ul>
      )}

      <AddSomeone onMade={(m) => { setMade(m); load(); }} />

      {dead.length > 0 && (
        <details className="share-archive">
          <summary>
            {dead.length} link{dead.length === 1 ? '' : 's'} no longer working
          </summary>
          <div className="stack" style={{ marginTop: 'var(--space-3)' }}>
            <p className="faint">These stopped working when you switched them off, or when they ran out. Kept so you can still see who had one and when they opened it.</p>
            <ul className="stack stack-lg" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {dead.map((s) => (
                <PersonCard key={s.id} share={s} onChanged={load} dead />
              ))}
            </ul>
          </div>
        </details>
      )}

      <p className="faint">
        A partner's page never carries a worksheet, a fuller line, a topic word or a weekly reading that names them, whatever the link's level. Nobody in your People list is ever on a link. The only thing a person can do on their page besides read is tap once to say they're thinking of you.
      </p>
    </div>
  );
}

/* --- The link, the one time it is shown in full ----------------------------- */

function MadeLink({ made, onDone }: { made: Share & { url: string }; onDone: () => void }): JSX.Element {
  const [copied, setCopied] = useState<'no' | 'yes' | 'failed'>('no');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    ref.current?.focus();
  }, []);
  return (
    <section className="card stack">
      <h2>Copy this link now</h2>
      <div className="banner banner-caution">
        Anyone who has it can read what you chose, without signing in. Send it the way you would send anything private. If you lose it, <strong>Copy the link</strong> on {made.label}'s card shows it again — the app keeps it encrypted, the way it keeps your entries.
      </div>
      <div className="field">
        <label htmlFor="new-link">The link for {made.label}</label>
        <input id="new-link" ref={ref} className="input" readOnly value={made.url} onFocus={(e) => e.currentTarget.select()} />
      </div>
      <button
        className="btn btn-primary btn-block"
        onClick={() => void navigator.clipboard.writeText(made.url).then(() => setCopied('yes')).catch(() => setCopied('failed'))}
      >
        {copied === 'yes' ? 'Copied' : 'Copy the link'}
      </button>
      {copied === 'failed' && <div className="banner banner-caution">This browser wouldn't let the page use your clipboard. Select the link above and copy it yourself.</div>}
      <button className="btn btn-ghost btn-block" onClick={() => { if (copied === 'yes' || confirm('Hide the link? Copy the link on the card will show it again.')) onDone(); }}>
        I've saved it — hide the link
      </button>
    </section>
  );
}

/* --- One person ------------------------------------------------------------- */

function PersonCard({ share, onChanged, dead = false }: { share: Share; onChanged: () => void; dead?: boolean }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed' | 'gone'>('idle');
  const [fallback, setFallback] = useState<string | null>(null);
  const [log, setLog] = useState<ShareAccessLogEntry[] | null>(null);
  const [helps, setHelps] = useState(share.whatHelps ?? '');
  const [editingHelps, setEditingHelps] = useState(false);
  const [note, setNote] = useState(share.note ?? '');
  const [editingNote, setEditingNote] = useState(false);
  // The level sheet.
  const [level, setLevel] = useState<ShareLevel>(share.level);
  const [addons, setAddons] = useState<ShareAddon[]>(share.addons);
  const [asking, setAsking] = useState<{ ask: ConfirmationId[]; given: ConfirmationId[] } | null>(null);

  useEffect(() => { setLevel(share.level); setAddons(share.addons); setHelps(share.whatHelps ?? ''); setNote(share.note ?? ''); }, [share]);

  const doc = documentForRole(share.role);
  const sees: string[] = [];
  for (const l of Object.values(LEVEL_CONTENTS)) if (l.grants.every((g) => share.grants.includes(g))) sees.push(...l.items.map((i) => i.name));
  const addonsOn = share.addons.map((a) => ADDON_CONTENTS[a].name);
  const addonsOff = (Object.keys(ADDON_CONTENTS) as ShareAddon[]).filter((a) => !share.addons.includes(a)).map((a) => ADDON_CONTENTS[a].name);
  const levelsAbove = Object.values(LEVEL_CONTENTS).filter((l) => !l.grants.every((g) => share.grants.includes(g))).map((l) => l.name);
  const status = share.revokedAt ? `switched off ${shortDate(share.revokedAt)}` : share.expiresAt && Date.parse(share.expiresAt) < Date.now() ? `ran out ${shortDate(share.expiresAt)}` : null;

  async function patch(body: Parameters<typeof api.updateShareCircle>[1]): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.updateShareCircle(share.id, body);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not save.');
    } finally {
      setBusy(false);
    }
  }

  async function move(next: { level: ShareLevel; addons: ShareAddon[] }, confirmed: ConfirmationId[]): Promise<void> {
    const needed = confirmationsNeeded(share, next);
    const missing = needed.filter((c) => !confirmed.includes(c));
    if (missing.length > 0) {
      setAsking({ ask: needed, given: confirmed });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.changeShareLevel(share.id, { level: next.level, addons: next.addons, confirmed });
      setAsking(null);
      onChanged();
    } catch (e) {
      const more = neededIn(e);
      if (more) setAsking({ ask: more, given: confirmed });
      else setError(e instanceof ApiError ? e.message : "Couldn't change that link.");
    } finally {
      setBusy(false);
    }
  }

  async function copyLink(): Promise<void> {
    setFallback(null);
    try {
      const { url } = await api.shareUrl(share.id);
      try {
        await navigator.clipboard.writeText(url);
        setCopy('copied');
      } catch {
        setCopy('failed');
        setFallback(url);
      }
    } catch {
      setCopy('gone');
    }
  }

  async function showLog(): Promise<void> {
    if (log) { setLog(null); return; }
    try {
      setLog(await api.shareAccessLog(share.id));
    } catch {
      setError("Couldn't load when that link was opened.");
    }
  }

  async function revoke(): Promise<void> {
    if (!confirm(`Switch off the link for ${share.label}? It stops working straight away, and anyone holding it will only see that it is no longer valid. This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.revokeShare(share.id);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't switch that link off.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (!confirm(`Delete the record of ${share.label}'s link, and when it was opened? This cannot be undone.`)) return;
    try {
      await api.deleteShare(share.id);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't delete that.");
    }
  }

  return (
    <li className={`card stack-sm stack person-card${dead ? ' person-card-dead' : ''}`}>
      <div>
        <h2 style={{ marginBottom: 0 }}>{share.label}</h2>
        <span className="faint">
          {share.role ? ROLE_PRESETS[share.role].name.toLowerCase() : LEVEL_CONTENTS[share.level].name}
          {status ? ` · ${status}` : share.role === 'therapist' && share.sessionOn ? ` · next session ${shortDate(share.sessionOn)}` : ` · since ${shortDate(share.createdAt)}`}
          {share.lastAccessedAt ? ` · opened ${shortDate(share.lastAccessedAt)}` : ' · not opened yet'}
        </span>
      </div>

      <div className="sees">
        {[...new Set(sees)].slice(0, 4).map((x) => <span key={x}>{x}</span>)}
        {addonsOn.map((x) => <span key={x}>{x}</span>)}
        {share.whatHelpsOn && share.whatHelps && <span>what helps, in your words</span>}
        {share.noteOn && share.note && <span>a line from you</span>}
        {(levelsAbove.length > 0 || addonsOff.length > 0) && <span className="no">{[...levelsAbove, ...addonsOff].join(' · ')}</span>}
        {share.role === 'partner' && <span className="no">anything that names them</span>}
      </div>

      {!dead && (
        <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <Link className="btn btn-secondary btn-compact" to={`/share/${share.id}/preview`}>
            Open it as {share.label}
          </Link>
          {share.linkKept && (
            <button type="button" className="btn btn-secondary btn-compact" onClick={() => void copyLink()}>
              {copy === 'copied' ? 'Copied' : 'Copy the link'}
            </button>
          )}
          <button type="button" className="btn btn-ghost btn-compact" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? 'Done' : 'Change ›'}
          </button>
        </div>
      )}
      {copy === 'gone' && <p className="faint">This link was made before the app kept a copy, so it cannot be shown again. Switch it off and make a new one.</p>}
      {copy === 'failed' && fallback && (
        <div className="field">
          <span className="faint">This browser wouldn't let the page use your clipboard. Select it and copy it yourself:</span>
          <input className="input" readOnly value={fallback} onFocus={(e) => e.currentTarget.select()} />
        </div>
      )}

      {open && !dead && (
        <div className="share-sheet stack-sm stack">
          <div className="row-between">
            <strong>What {share.label} sees</strong>
            <span className="faint">each step asks you once</span>
          </div>
          <div className="chips">
            {LEVEL_ORDER.map((l) => (
              <button key={l} type="button" className="chip" aria-pressed={level === l} disabled={busy} onClick={() => { setLevel(l); void move({ level: l, addons }, asking?.given ?? []); }}>
                {LEVEL_CONTENTS[l].name}
              </button>
            ))}
          </div>
          <div className="chips">
            {shareAddon.options.map((a) => (
              <button key={a} type="button" className="chip" aria-pressed={addons.includes(a)} disabled={busy} onClick={() => { const next = addons.includes(a) ? addons.filter((x) => x !== a) : normaliseAddons([...addons, a]); setAddons(next); void move({ level, addons: next }, []); }}>
                + {ADDON_CONTENTS[a].name}
              </button>
            ))}
          </div>
          {asking && (
            <ConfirmSheet ids={asking.ask} busy={busy} onYes={() => void move({ level, addons }, [...asking.given, ...asking.ask])} onBack={() => { setAsking(null); setLevel(share.level); setAddons(share.addons); }} />
          )}
          <p className="faint">
            {share.detailSince ? `Fuller lines since ${shortDate(share.detailSince)}. ` : ''}
            Narrowing never asks. The link stays the same; {share.label} sees the change next time they open it.
          </p>

          {/* The role, settable on a link made before Circle existed (every link
              before 13 Oct has none): it picks the document they open first and,
              for a partner, the partner rule. Not a grant; asks nothing. */}
          <div className="row-between" style={{ flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            <span>Made for</span>
            <div className="chips">
              {(Object.keys(ROLE_PRESETS) as ShareRole[]).map((r) => (
                <button key={r} type="button" className="chip" aria-pressed={share.role === r} disabled={busy} onClick={() => void patch({ role: share.role === r ? null : r })}>
                  {ROLE_PRESETS[r].name}
                </button>
              ))}
            </div>
          </div>
          <span className="faint">
            {share.role === null
              ? `No role: ${share.label} opens the full page. A therapist or partner opens their document first; a partner's page also never carries anything that names them.`
              : share.role === 'friend'
                ? `A friend opens the full page.`
                : `${share.label} opens ${DOCUMENTS[documentForRole(share.role)!].name.toLowerCase()} first, with the full page under it.`}
          </span>

          <div className="row-between">
            <span>{share.expiresAt === null ? 'No end date' : `Runs until ${shortDate(share.expiresAt)}`}</span>
            <span className="faint">to change it, make a new link</span>
          </div>
          {share.visibleFrom && <span className="faint">Shows entries from {shortDate(share.visibleFrom)} on.</span>}
          {share.requireAccount && <span className="faint">Opens only for {share.granteeEmail ?? 'the named address'}, signed in.</span>}

          {share.role === 'therapist' && (
            <div className="row-between" style={{ gap: 'var(--space-2)' }}>
              <label htmlFor={`session-${share.id}`}>Next session</label>
              <input id={`session-${share.id}`} type="date" className="input" style={{ maxWidth: '11rem' }} value={share.sessionOn ?? ''} disabled={busy} onChange={(e) => void patch({ sessionOn: e.target.value || null })} />
            </div>
          )}
          {share.role === 'therapist' && (
            <span className="faint">{share.lastSessionOn ? `The brief runs from the last one, ${shortDate(share.lastSessionOn)}.` : 'The brief covers the last fortnight until a session has passed.'}</span>
          )}

          <div className="row-between">
            <span>
              Opened {share.accessCount} time{share.accessCount === 1 ? '' : 's'}
              {share.lastAccessedAt ? ` · last ${shortDateTime(share.lastAccessedAt)}` : ''}
            </span>
            <button type="button" className="linkish" onClick={() => void showLog()}>
              {log ? 'Hide the log' : 'The log ›'}
            </button>
          </div>
          {log && (
            <ul className="share-log">
              {log.length === 0 ? <li className="faint">Never opened.</li> : log.slice(0, 30).map((e) => (
                <li key={e.id}>
                  <span>{shortDateTime(e.accessedAt)}</span>
                  <span className="faint">{e.resource === 'view' ? 'the page' : e.resource === 'wave' ? 'thinking of you' : e.resource === 'brief' || e.resource === 'week' ? DOCUMENTS[e.resource].name.toLowerCase() : e.resource}{e.userAgent ? ` · ${browserOf(e.userAgent)}` : ''}</span>
                </li>
              ))}
            </ul>
          )}

          {share.changes.length > 0 && (
            <details className="share-archive">
              <summary>{share.changes.length} change{share.changes.length === 1 ? '' : 's'} in the last 30 days</summary>
              <ul className="share-log" style={{ marginTop: 'var(--space-2)' }}>
                {share.changes.map((c, i) => (
                  <li key={i}>
                    <span>{shortDate(c.changedAt)}</span>
                    <span className="faint">{c.fromLevel ? `${LEVEL_CONTENTS[c.fromLevel].name} → ` : ''}{LEVEL_CONTENTS[c.toLevel].name}{c.toAddons.length ? ` + ${c.toAddons.map((a) => ADDON_CONTENTS[a].name).join(', ')}` : ''}{c.by === 'update' ? ' · by an update to the app' : ''}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          <label className="option option-toggle">
            <input type="checkbox" checked={share.whatHelpsOn} disabled={busy || !share.whatHelps} onChange={(e) => void patch({ whatHelpsOn: e.target.checked })} />
            <span><strong>What helps, in your words</strong><span className="muted"> — on their page while this is on.</span></span>
          </label>
          {editingHelps ? (
            <>
              <textarea className="textarea" rows={3} value={helps} maxLength={600} onChange={(e) => setHelps(e.target.value)} placeholder="What helps when it's hard, in your words. Or leave it empty." />
              <div className="row" style={{ gap: 'var(--space-2)' }}>
                <button type="button" className="btn btn-primary grow" disabled={busy} onClick={() => void patch({ whatHelps: helps.trim() || null }).then(() => setEditingHelps(false))}>Save</button>
                <button type="button" className="btn btn-ghost" onClick={() => { setHelps(share.whatHelps ?? ''); setEditingHelps(false); }}>Keep it</button>
              </div>
            </>
          ) : (
            <div className="row-between">
              <span className={share.whatHelps ? 'reading' : 'faint'}>{share.whatHelps ?? 'Nothing written yet.'}</span>
              <button type="button" className="btn btn-ghost btn-compact" onClick={() => setEditingHelps(true)}>{share.whatHelps ? 'Change' : 'Write it'}</button>
            </div>
          )}
          <label className="option option-toggle">
            <input type="checkbox" checked={share.noteOn} disabled={busy || !share.note} onChange={(e) => void patch({ noteOn: e.target.checked })} />
            <span><strong>A line from you</strong><span className="muted"> — anything you want {share.label} to read when they open the page. Shown until you change it.</span></span>
          </label>
          {editingNote ? (
            <>
              <textarea className="textarea" rows={3} value={note} maxLength={600} onChange={(e) => setNote(e.target.value)} placeholder={`For ${share.label}, in your words. Or leave it empty.`} />
              <div className="row" style={{ gap: 'var(--space-2)' }}>
                <button type="button" className="btn btn-primary grow" disabled={busy} onClick={() => void patch({ note: note.trim() || null }).then(() => setEditingNote(false))}>Save</button>
                <button type="button" className="btn btn-ghost" onClick={() => { setNote(share.note ?? ''); setEditingNote(false); }}>Keep it</button>
              </div>
            </>
          ) : (
            <div className="row-between">
              <span className={share.note ? 'reading' : 'faint'}>{share.note ?? 'Nothing written yet.'}</span>
              <button type="button" className="btn btn-ghost btn-compact" onClick={() => setEditingNote(true)}>{share.note ? 'Change' : 'Write it'}</button>
            </div>
          )}
          <label className="option option-toggle">
            <input type="checkbox" checked={share.wavesOn} disabled={busy} onChange={(e) => void patch({ wavesOn: e.target.checked })} />
            <span><strong>Their page can say “thinking of you”</strong><span className="muted"> — one tap, one line on your Today. No message, no reply.</span></span>
          </label>
          {doc && (
            <Link className="linkish" to={`/share/${share.id}/preview/${doc}`}>
              {DOCUMENTS[doc].name}, as {share.label} opens it ›
            </Link>
          )}
          <button type="button" className="linkish share-off" disabled={busy} onClick={() => void revoke()}>
            Switch this link off
          </button>
        </div>
      )}

      {dead && (
        <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <button type="button" className="linkish" onClick={() => void showLog()}>{log ? 'Hide the log' : `Opened ${share.accessCount} time${share.accessCount === 1 ? '' : 's'} ›`}</button>
          <button type="button" className="linkish linkish-quiet" onClick={() => void remove()}>Delete the record</button>
        </div>
      )}
      {dead && log && (
        <ul className="share-log">
          {log.length === 0 ? <li className="faint">Never opened.</li> : log.slice(0, 30).map((e) => (
            <li key={e.id}><span>{shortDateTime(e.accessedAt)}</span><span className="faint">{e.resource}</span></li>
          ))}
        </ul>
      )}
      {error && <p className="faint" role="alert">{error}</p>}
    </li>
  );
}

/* --- Add someone ------------------------------------------------------------- */

function AddSomeone({ onMade }: { onMade: (m: Share & { url: string }) => void }): JSX.Element {
  const [role, setRole] = useState<ShareRole | 'other' | null>(null);
  const [name, setName] = useState('');
  const [level, setLevel] = useState<ShareLevel>('seem');
  const [addons, setAddons] = useState<ShareAddon[]>([]);
  const [expiresInDays, setExpiresInDays] = useState<number | null>(90);
  const [since, setSince] = useState('');
  const [requireAccount, setRequireAccount] = useState(false);
  const [email, setEmail] = useState('');
  const [asking, setAsking] = useState<{ ask: ConfirmationId[]; given: ConfirmationId[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = (r: ShareRole | 'other'): void => {
    setRole(r);
    setAsking(null);
    if (r !== 'other') { setLevel(ROLE_PRESETS[r].level); setAddons([...ROLE_PRESETS[r].addons]); }
    else { setLevel('seem'); setAddons([]); }
  };
  const reset = (): void => { setRole(null); setName(''); setAsking(null); setError(null); setSince(''); setRequireAccount(false); setEmail(''); setExpiresInDays(90); };

  async function create(confirmed: ConfirmationId[]): Promise<void> {
    if (!role || !name.trim()) return;
    const needed = confirmationsNeeded(null, { level, addons });
    if (needed.some((c) => !confirmed.includes(c))) { setAsking({ ask: needed, given: confirmed }); return; }
    setBusy(true);
    setError(null);
    try {
      const made = await api.createShare({
        label: name.trim(),
        level,
        addons,
        confirmed,
        role: role === 'other' ? null : role,
        expiresInDays,
        visibleFrom: since ? `${since}T00:00:00Z` : null,
        requireAccount,
        granteeEmail: requireAccount ? email.trim() : null,
      });
      onMade(made);
      reset();
    } catch (e) {
      const more = neededIn(e);
      if (more) setAsking({ ask: more, given: confirmed });
      else setError(e instanceof ApiError ? e.message : 'Could not make the link.');
    } finally {
      setBusy(false);
    }
  }

  const blocked = !name.trim() ? 'Give it a name first.' : requireAccount && !email.trim() ? 'Add the address they will sign in with, or turn off the sign-in requirement.' : null;

  if (!role) {
    return (
      <section className="stack-sm stack">
        <h2>Add someone</h2>
        <div className="chips">
          {(Object.keys(ROLE_PRESETS) as ShareRole[]).map((r) => (
            <button key={r} type="button" className="chip" onClick={() => pick(r)}>{ROLE_PRESETS[r].name}</button>
          ))}
          <button type="button" className="chip" onClick={() => pick('other')}>Someone else…</button>
        </div>
        <p className="faint">A role is a starting point: it fills in the level and add-ons. Each step above How I seem and each add-on still asks you first. “Someone else…” is the full ladder with no role.</p>
      </section>
    );
  }

  const title = role === 'other' ? 'Someone else' : ROLE_PRESETS[role].name;
  return (
    <section className="card stack-sm stack">
      <h2>{title}</h2>
      {role !== 'other' && <p className="muted" style={{ margin: 0 }}>{ROLE_PRESETS[role].blurb}</p>}
      <div className="field">
        <label htmlFor="circle-name">Their name</label>
        <input id="circle-name" className="input" value={name} maxLength={60} placeholder="As you'd say it" onChange={(e) => setName(e.target.value)} autoFocus />
        <span className="hint">They see this at the top of their page, so use a name you would be happy for them to read.</span>
      </div>

      <div className="row-between"><strong>What {name.trim() || 'they'} will see</strong><span className="faint">each step asks you once</span></div>
      <div className="chips">
        {LEVEL_ORDER.map((l) => (
          <button key={l} type="button" className="chip" aria-pressed={level === l} onClick={() => { setLevel(l); setAsking(null); }}>{LEVEL_CONTENTS[l].name}</button>
        ))}
      </div>
      <div className="chips">
        {shareAddon.options.map((a) => (
          <button key={a} type="button" className="chip" aria-pressed={addons.includes(a)} onClick={() => { setAddons(addons.includes(a) ? addons.filter((x) => x !== a) : normaliseAddons([...addons, a])); setAsking(null); }}>+ {ADDON_CONTENTS[a].name}</button>
        ))}
      </div>
      <ul className="share-will-see">
        {itemsFor(level, addons).map((i) => (
          <li key={i.id}><span className="share-will-see-name">{i.name}</span><span className="share-will-see-what">{i.what}</span></li>
        ))}
      </ul>
      {itemsWithheld(level, addons).length > 0 && <p className="faint">Not on this link: {itemsWithheld(level, addons).map((i) => i.name).join(', ')}.</p>}

      <div className="row-between" style={{ flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        <span>How long it works</span>
        <div className="chips">
          {EXPIRY_OPTIONS.map((o) => (
            <button key={String(o.days)} type="button" className="chip" aria-pressed={expiresInDays === o.days} onClick={() => setExpiresInDays(o.days)}>{o.label}</button>
          ))}
        </div>
      </div>
      {expiresInDays === null && <div className="banner banner-caution">A link with no end keeps working until you switch it off — through forwarded messages, inbox backups and a change of clinician. Choose this only if you mean it.</div>}

      <details className="share-archive">
        <summary>Only show entries from a date · make them sign in</summary>
        <div className="stack-sm stack" style={{ marginTop: 'var(--space-2)' }}>
          <div className="field">
            <label htmlFor="circle-since">Only show entries from this date</label>
            <input id="circle-since" className="input" type="date" value={since} max={new Date().toLocaleDateString('en-CA')} onChange={(e) => setSince(e.target.value)} />
            <span className="hint">Optional. For handing over only what has happened since the last appointment.</span>
          </div>
          <label className="option option-toggle">
            <input type="checkbox" checked={requireAccount} onChange={() => setRequireAccount(!requireAccount)} />
            <span><strong>Make them sign in first</strong><span className="muted"> — the link then opens only for the address below, signed in on this server. Forwarded, it gets nobody anything.</span></span>
          </label>
          {requireAccount && (
            <div className="field">
              <label htmlFor="circle-email">The address they'll sign in with</label>
              <input id="circle-email" className="input" type="email" inputMode="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          )}
        </div>
      </details>

      {asking ? (
        <ConfirmSheet ids={asking.ask} busy={busy} onYes={() => void create([...asking.given, ...asking.ask])} onBack={() => setAsking(null)} />
      ) : (
        <button type="button" className="btn btn-primary btn-block" disabled={busy || blocked !== null} onClick={() => void create([])}>
          {busy ? <span className="spinner" /> : 'Make the link'}
        </button>
      )}
      {blocked && !asking && <p className="faint" style={{ textAlign: 'center' }}>{blocked}</p>}
      {error && <p className="faint" role="alert">{error}</p>}
      <button type="button" className="btn btn-ghost btn-block" onClick={reset}>Not now</button>
    </section>
  );
}

function ConfirmSheet({ ids, busy, onYes, onBack }: { ids: ConfirmationId[]; busy: boolean; onYes: () => void; onBack: () => void }): JSX.Element {
  const head = useRef<HTMLHeadingElement>(null);
  useEffect(() => head.current?.focus(), []);
  return (
    <section className="card stack share-confirm" aria-labelledby="share-confirm-head">
      <h3 id="share-confirm-head" ref={head} tabIndex={-1}>Before you share this</h3>
      <ul className="share-will-see">
        {ids.map((id) => (
          <li key={id}><span className="share-will-see-name">{confirmationName(id)}</span><span className="share-will-see-what">{CONFIRMATIONS[id]}</span></li>
        ))}
      </ul>
      <button type="button" className="btn btn-primary btn-block" onClick={onYes} disabled={busy}>{busy ? <span className="spinner" /> : 'Yes, share this'}</button>
      <button type="button" className="btn btn-ghost btn-block" onClick={onBack} disabled={busy}>Go back</button>
    </section>
  );
}

/**
 * A date, with the year whenever it is not this one.
 *
 * It used to be day-and-month always. A link set to run a year shows its end
 * date here, so Katie's — which expires in September 2027 — read
 * "Runs until 21 Sep", a date that could as easily have been last September
 * and already gone. Nick asked which year it meant, which is the whole
 * argument for this function.
 *
 * Dates inside the current year keep the short form: the year is noise when
 * it is the one you are in, and these lines sit on a crowded card.
 */
function shortDate(iso: string): string {
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}
function shortDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}
function browserOf(ua: string): string {
  if (/iPhone|iPad/.test(ua)) return 'iPhone';
  if (/Android/.test(ua)) return 'Android';
  if (/Safari/.test(ua) && !/Chrome/.test(ua)) return 'Safari';
  if (/Firefox/.test(ua)) return 'Firefox';
  if (/Chrome/.test(ua)) return 'Chrome';
  return 'a browser';
}
